import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  fetchIdempotencyStats,
  generateIdempotencyKey,
  idempotencyScopeMeta,
  idempotencyStatusMeta,
  IDEMPOTENCY_SCOPES,
  listIdempotencyKeys,
  withIdempotency,
  type CoreIdempotencyKey,
} from '@/lib/coreIdempotency';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function IdempotenceTab({ org }: Props) {
  const [keys, setKeys] = useState<CoreIdempotencyKey[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, inProgress: 0, failed: 0, duplicatesPrevented: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [k, s] = await Promise.all([listIdempotencyKeys(org.id, 60), fetchIdempotencyStats(org.id)]);
      setKeys(k);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  /** Démonstration : deux appels avec la MÊME clé ne produisent qu'un seul effet. */
  const runDemo = async () => {
    setRunning(true);
    setLog([]);
    const key = generateIdempotencyKey('demo');
    let created = 0;
    const executor = async () => {
      created += 1;
      return { response: { created: true, creationNumber: created }, entityType: 'demo', entityId: `demo_${created}` };
    };
    try {
      const first = await withIdempotency(
        { tenantId: org.tenant_id, organizationId: org.id, scope: 'orders', key, requestPayload: { demo: true } },
        executor,
      );
      const second = await withIdempotency(
        { tenantId: org.tenant_id, organizationId: org.id, scope: 'orders', key, requestPayload: { demo: true } },
        executor,
      );
      setLog([
        `1er appel  → duplicate=${first.duplicate} · réponse=${JSON.stringify(first.response)}`,
        `2e appel   → duplicate=${second.duplicate} · réponse=${JSON.stringify(second.response)}`,
        created === 1
          ? '✓ Résultat : l’action n’a été exécutée QU’UNE fois. Le rejeu a renvoyé la réponse mémorisée.'
          : `✗ Résultat : l’action a été exécutée ${created} fois (idempotence non respectée).`,
      ]);
      load();
    } catch (e) {
      setLog([`Erreur : ${e instanceof Error ? e.message : 'inconnue'}`]);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Idempotence</span> : si une action critique (commande, réservation, paiement, rendez-vous, facture)
        est envoyée deux fois à cause d’un problème réseau, elle ne doit produire qu’<span className="font-semibold text-foreground-700">un seul</span> effet.
        On s’appuie sur une <code className="bg-background-100 px-1 rounded">idempotency_key</code> mémorisée avec sa réponse.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {['Requête', 'idempotency_key', 'Déjà vue ?', 'Renvoi de la réponse'].map((step, i, arr) => (
            <span key={step} className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full font-medium whitespace-nowrap ${i === 1 ? 'bg-accent-100 text-accent-900' : i === 3 ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-700'}`}>{step}</span>
              {i < arr.length - 1 && <i className="ri-arrow-right-line text-foreground-300"></i>}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Clés mémorisées', value: stats.total, icon: 'ri-fingerprint-line', color: 'text-primary-600' },
          { label: 'Terminées', value: stats.completed, icon: 'ri-checkbox-circle-line', color: 'text-primary-600' },
          { label: 'En cours', value: stats.inProgress, icon: 'ri-loader-4-line', color: 'text-accent-600' },
          { label: 'Échouées (rejouables)', value: stats.failed, icon: 'ri-refresh-line', color: 'text-secondary-600' },
        ].map((c) => (
          <div key={c.label} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
            <i className={`${c.icon} text-lg ${c.color}`}></i>
            <p className="text-lg font-bold text-foreground-900 mt-1">{c.value}</p>
            <p className="text-xs text-foreground-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-loop-right-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Démonstration : double envoi</h4>
          <button onClick={runDemo} disabled={running} className="ml-auto px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-play-line"></i>} Simuler 2 envois
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {IDEMPOTENCY_SCOPES.map((s) => (
              <span key={s.key} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${s.className}`}>
                <i className={s.icon}></i>{s.label}
              </span>
            ))}
          </div>
          {log.length > 0 && (
            <pre className="rounded-md bg-background-100 p-3 text-[11px] text-foreground-700 whitespace-pre-wrap break-words">{log.join('\n')}</pre>
          )}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Clés d’idempotence</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : keys.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune clé mémorisée. Lancez la démonstration.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[380px] overflow-y-auto">
            {keys.map((k) => {
              const meta = idempotencyStatusMeta(k.status);
              const scope = idempotencyScopeMeta(k.scope);
              return (
                <div key={k.id} className="flex items-center gap-3 px-4 py-2.5">
                  <i className={`${scope.icon} text-foreground-500`}></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-800 truncate font-mono">{k.idempotency_key}</p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {scope.label}{k.entity_type ? ` · ${k.entity_type}#${k.entity_id}` : ''}
                      {k.created_at ? ` · ${new Date(k.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}